import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  EffectRef,
  ElementRef,
  inject,
  linkedSignal,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { PanelModule } from 'primeng/panel';
import { DmRangeDatePickerLibComponent, endDateRequired } from '@everymatrix-base-components/dm-range-date-picker-lib';
import { DmInputTextLibComponent } from '@everymatrix-base-components/dm-input-text-lib';
import { DmSelectLibComponent } from '@everymatrix-base-components/dm-select-lib';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import { VerificationService } from './services/verification.service';
import { DmTreeTableComponent } from '@everymatrix-base-components/dm-tree-table';
import { DmUiGridLibComponent } from '@everymatrix-base-components/dm-ui-grid-lib';
import { catchError, combineLatest, filter, fromEvent, map, mergeMap, of, switchMap, take } from 'rxjs';
import { NgStyle } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import {
  downloadURL,
  DynamicFilters,
  Lookups,
  REPORTS,
  ReportTemplate,
  ShadowDomStyleInjectorService,
  TableHeaderColumn,
  TemplateService,
  SessionService,
  TracingInfoService,
  ReportSearchParams,
  TEMPORARY_TEMPLATE_WARNING,
  FormSnapshot
} from '@everymatrix-base-components/utils';
import { Dialog } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import moment from 'moment';
import {
  ToggleSwitchChangeEvent,
  ToggleSwitchModule,
} from 'primeng/toggleswitch';
import {
  DomainsOverloadModalComponent,
  ReportConfigurationModalComponent,
  SaveTemplateModalComponent,
  TemplateSelectModalComponent,
  ToastMessageComponent,
} from '@everymatrix-base-components/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Checkbox } from 'primeng/checkbox';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    PanelModule,
    DmRangeDatePickerLibComponent,
    DmInputTextLibComponent,
    DmSelectLibComponent,
    DmButtonLibComponent,
    ReactiveFormsModule,
    DmTreeTableComponent,
    ProgressSpinnerModule,
    NgStyle,
    Dialog,
    TooltipModule,
    ToastMessageComponent,
    DmUiGridLibComponent,
    ToggleSwitchModule,
    FormsModule,
    SaveTemplateModalComponent,
    ReportConfigurationModalComponent,
    TemplateSelectModalComponent,
    DomainsOverloadModalComponent,
    Checkbox,
  ],
  providers: [TemplateService],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class AppComponent implements OnInit, AfterViewInit {
  private templateService = inject(TemplateService);
  private verificationService = inject(VerificationService);
  private sessionService = inject(SessionService);
  private tracingInfoService = inject(TracingInfoService);
  private el = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private shadowDomStyleInjectorService = inject(ShadowDomStyleInjectorService);
  attachedToShadowDom = signal(false);
  isTemplateSaved = signal(true);
  csvLoading = signal(false);
  templateCsvLoadingConfig = signal({ templateId: '', isLoading: false });
  configureReportModalVisible = signal(false);
  modalVisible = signal(false);
  domainsOverloadVisible = signal(false);
  lookupsSignal = signal<{ [key: string]: Lookups[] } | undefined>(undefined);
  domainLookupsSignal = signal<Lookups[] | undefined>(undefined);
  allFieldsSignal = signal<DynamicFilters[] | undefined>(undefined);
  templateSignal = this.templateService.template;
  ready = computed(() => {
    return (
      this.templateSignal() !== undefined &&
      this.lookupsSignal() !== undefined &&
      this.domainLookupsSignal() !== undefined &&
      this.allFieldsSignal() !== undefined
    );
  });
  destroyRef = inject(DestroyRef);
  visibleFieldsSignal: any = linkedSignal({
    source: () => this.allFieldsSignal(),
    computation: (all) => {
      if (all) {
        const selectedIds =
          this.templateSignal()?.input_params.visibleFields ??
          all?.filter((f) => f.is_default === 'true').map((f) => f.field_id);

        return selectedIds.reduce((acc: DynamicFilters[], id: string) => {
          const field = all.find(({ field_id }) => field_id == id);
          if (field) acc.push(field);
          return acc
        }, []);
      }
      return undefined;
    },
  });
  allConfigurationFields!: DynamicFilters[];
  prevVisibleFields: DynamicFilters[] = [];
  requiredFields = [
    'merchant_name',
    'verification_creation_date',
    'verification_updated_date',
  ];
  documentFieldIds = [
    'documents_type',
    'documents_status',
    'documents_expiry_date',
    'documents_created_date',
  ];
  templateSavedStateSnapshot!: boolean;
  filterFormSnapshot: FormSnapshot | null = null;
  controlSnapshots = new Map<string, FormSnapshot>();
  tableColumns: TableHeaderColumn[] = [];
  filterForm: any;
  isPanelOpened = true;
  tableData: any[] = [];
  generalTotal = '';
  limit = signal(100);
  offset = 0;
  scrollHeight = 350;
  showReportTable = signal(false);
  tableLoading = false;
  runState = false;
  documentsModal = false;
  isOldTableStyle = false;
  domainsOverloadSaveMode = false;
  domainsOverloadAction: 'Run' | 'Download' | 'Save' | '' = '';
  gridApi: any;
  orderBy: any;
  orderDirection: any;
  reportId = REPORTS.MM_VERIFICATION.reportId;
  effectRef: EffectRef;
  maxDay: Date = moment().endOf('year').toDate();
  gridOptions: any = {
    infiniteScrollDown: false,
    enableSorting: true,
    showGridFooter: false,
    showColumnFooter: true,
    enableFiltering: false,
    enableColumnResizing: true,
    enableGridMenu: true,
    exporterMenuCsv: false,
    exporterMenuPdf: false,
    exporterMenuExcel: false,
    treeRowHeaderAlwaysVisible: true,
    columnDefs: [],
    data: [],
    enableHorizontalScrollbar: 2,
    gridFooterTemplate: `<div class="ui-grid-footer-info ui-grid-grid-footer">
                    <div ng-if="grid.loadingAggregationData">Loading total...</div>
                    <div ng-if="!grid.loadingAggregationData">Total: {{grid.total}} (visible: {{grid.rows.length}})</div>
                </div>`,
    enableRowHeaderSelection: false,
    useExternalSorting: true,
    dataLoading: this.tableLoading,
    isVisible: false,
    paginationPageSize: this.limit(),
    paginationPageSizes: [10, 20, 30, 50, 100, 250, 500],
    useExternalPagination: true,
    totalItems: 0,
    paginationCurrentPage: 1,
    scrollHeight: 360,
    onRegisterApi: (gridApi: any) => {
      this.gridApi = gridApi;
      this.gridApi.core.on.sortChanged(null, (grid: any, sortColumn: any) => {
        if (sortColumn.length > 1) {
          let column = null;
          for (let j = 0; j < grid.columns.length; j++) {
            if (grid.columns[j].name === sortColumn[0].displayName) {
              column = grid.columns[j];
              break;
            }
          }
          if (column) {
            sortColumn[1].sort.priority = 1;
            column.unsort();
          }
        } else if (sortColumn && sortColumn[0]) {
          this.onSort({
            field: sortColumn.slice(-1)[0].field,
            value: sortColumn[0].sort.direction,
          });
        }
      });
      if (this.gridApi && this.gridApi.treeBase && this.gridApi.treeBase.expandAllRows) {
        this.gridApi.grid.registerDataChangeCallback(() =>
          this.gridApi.treeBase.expandAllRows()
        );
      }

      this.gridApi.pagination.on.paginationChanged(
        null,
        (newPage: number, pageSize: number) => {
          // do not trigger data loading if only need to set correct page
          if (
            this.limit() === pageSize &&
            (newPage - 1) * pageSize == this.offset
          )
            return;

          this.limit.set(pageSize);
          this.runReport((newPage - 1) * pageSize, true);
        }
      );
    },
  };

  constructor() {
    this.sessionService.resetSession();
    this.tracingInfoService.resetTracingInfo();
    this.effectRef = effect(
      () => {
        if (!this.ready() || this.filterForm) {
          return;
        }
        const tpl = this.templateSignal();
        const all = this.allFieldsSignal();
        const visibleFields = this.visibleFieldsSignal();
        this.buildFilterForm(
          all as DynamicFilters[],
          visibleFields,
          tpl?.input_params.fieldValues
        );
        this.showReportTable.set(false);
        this.tableLoading = false;
        this.effectRef.destroy();
      },
      { manualCleanup: true }
    );
  }

  ngOnInit() {
    this.initializeApp();
    //Executed when the back/forward buttons are pressed
    fromEvent(window, 'popstate')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleRouteChange());

    this.templateService.templateChangedSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((template: ReportTemplate | null) => {
        if (template) {
          this.updateReport(template);
        }
      });
  }

  private initializeApp() {
    this.sessionService.sessionData$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data) => data !== null),
        take(1),
        mergeMap(() =>
          combineLatest({
            lookups: this.verificationService.getLookups(),
            domainLookups: this.verificationService
              .getDomainLookups()
              .pipe(map((d) => d.domains_mm)),
            columns: this.verificationService.getReportFilters(),
          })
        )
      )
      .subscribe((data) => {
        if (data?.lookups) {
          if (!this.lookupsSignal()) {
            const groupedLookups = this.groupLookups(data.lookups);
            this.sortGroupedLookups(groupedLookups);
            this.lookupsSignal.set(groupedLookups);
          }
        }
        if (data?.domainLookups) {
          if (!this.domainLookupsSignal()) {
            this.domainLookupsSignal.set(data.domainLookups);
          }
        }

        if (data?.columns) {
          if (!this.allFieldsSignal()) {
            const result = this.sortRequiredFields(data.columns.result);
            result.forEach((col: DynamicFilters) => {
              if (this.requiredFields.includes(col.field_id)) {
                col.required = true;
              }
            });
            this.allFieldsSignal.set(result);
            this.allFieldsForConfiguration(result);
          }
        }
        this.processTemplateIdFromUrl();
      });
  }

  private handleRouteChange = () => {
    const params = new URLSearchParams(window.location.search);
    const templateId = params.get('templateId');
    const modificationId = params.get('modificationId');
    const id = modificationId || templateId;
    if (id) {
      const currentTemplate = (
        this.templateService.templatesList() as ReportTemplate[]
      )?.find((item) => item.template_id === id);
      this.templateSignal.set(currentTemplate);
      this.templateService.templateChangedSubject.next(
        currentTemplate as ReportTemplate
      );
    } else {
      this.clearTemplate();
    }
  };

  ngAfterViewInit() {
    this.shadowDomStyleInjectorService.injectUiGridStyles(this.el);
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.el)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (injected) => this.attachedToShadowDom.set(injected),
        error: (err) => console.error(err),
      });
  }

  applyTemplate(template: ReportTemplate) {
    const allFields = this.allFieldsSignal();
    const visibleFields: DynamicFilters[] =
      template.input_params.visibleFields.reduce(
        (acc: DynamicFilters[], fieldId: string) => {
          const field = allFields?.find(({ field_id }) => field_id == fieldId);
          if (field) acc.push(field);
          return acc;
        },
        []
      );

    if (this.filterForm) {
      this.filterForm.patchValue(template.input_params.fieldValues);
    } else {
      this.buildFilterForm(
        allFields as DynamicFilters[],
        visibleFields,
        template.input_params.fieldValues
      );
    }
    this.visibleFieldsSignal.set(visibleFields);
    this.filterForm.markAsDirty();
    this.filterForm.markAsUntouched();
  }

  updateReport(template: ReportTemplate) {
    this.applyTemplate(template);
    this.runState = false;
    this.showReportTable.set(false);
    this.isTemplateSaved.set(true);
  }

  private processTemplateIdFromUrl() {
    const queryParams = new URLSearchParams(window.location.search);
    const templateId = queryParams.get('templateId');
    const modificationId = queryParams.get('modificationId');
    const id = modificationId || templateId;

    if (id) {
      this.templateService
        .getTemplateById(this.reportId, id)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((template) => {
          if (template) {
            this.templateSignal.set(template);
            this.applyTemplate(template);
          } else {
            this.clearTemplate();
          }
        });
    } else {
      this.clearTemplate();
    }
  }

  protected saveTemporaryTemplate() {
    const payload = {
      fieldValues: this.filterForm.value,
      visibleFields: this.visibleFieldsId,
      allFields: this.allFieldsSignal(),
    };
    const currentTemplate = this.templateSignal();
    const baseTemplate = this.templateService.baseTemplate();

    if (!this.isTemplateSaved() || !currentTemplate) {
      this.templateService
        .saveTemplate(null, null, payload, this.reportId, null, true)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((template) => {
          const params: ReportSearchParams =
            currentTemplate &&
            ((!currentTemplate.is_temporary &&
              currentTemplate.template_name &&
              template.template_id) ||
              (baseTemplate &&
                currentTemplate.is_temporary &&
                baseTemplate.template_id !== template.template_id))
              ? {
                  modificationId: template.template_id,
                  templateName: baseTemplate?.template_name || currentTemplate.template_name,
                  templateId: baseTemplate?.template_id || currentTemplate.template_id,
                }
              : {
                  modificationId: null,
                  templateName: null,
                  templateId: template.template_id,
                };
          this.templateService.setSearchParams(params);
          this.templateService.updateTemplateList(template);
          this.templateSignal.set(template);
          this.applyTemplate(template);
        });
    }
  }

  private sortRequiredFields(columns: DynamicFilters[]) {
    const mandatoryLength = this.requiredFields.length;
    const getPriority = (fieldId: string) => {
      const index = this.requiredFields.indexOf(fieldId);
      return index !== -1 ? index : mandatoryLength;
    };

    columns.sort((a, b) => {
      return getPriority(a.field_id) - getPriority(b.field_id);
    });

    return columns;
  }

  private buildFilterForm(
    all: DynamicFilters[],
    visibleFields: DynamicFilters[],
    values?: Record<string, unknown>,
  ) {
    this.filterForm = new FormGroup({});
    const fieldsToExclude = [
      'verification_updated_date',
      'documents_expiry_date',
      'documents_created_date',
    ];
    const visibleFieldsIds = visibleFields.map((item)=> item.field_id);
    all.forEach((field) => {
      let def = (values && values[field.field_id]) || null;

      const validators = [];

      if (field.field_id === 'merchant_name') {
        validators.push(Validators.required);
        if (!values) {
          def = (this.domainLookupsSignal() as Lookups[]).map(
            (l) => l.lookup_key
          );
        }
      }
      if (!values && field.field_data_type === 'TIMESTAMP' && !fieldsToExclude.includes(field.field_id) && visibleFieldsIds.includes(field.field_id)) {
        def = {
          alias: 'custom',
          start: moment().startOf('day').toDate(),
          end: moment().endOf('day').toDate(),
        };
      }

      if(field.field_data_type === 'TIMESTAMP') {
        validators.push(endDateRequired)
      }

      this.filterForm.addControl(
        field.field_id,
        new FormControl(def, validators)
      );
    });

    this.filterForm.addControl('show_last_verification', new FormControl(values && !this.nullUndefinedOrEmpty(values['show_last_verification']) ? !!values['show_last_verification'] : true));

    if (values) {
      this.isTemplateSaved.set(true);
    }

    this.filterForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.isTemplateSaved.set(false));
  }

  private buildOldTableColumns(rawColumns: DynamicFilters[]): void {
    this.gridOptions.columnDefs = rawColumns.map((item, index) => {
      return {
        name: item.field_name,
        displayName: item.field_name,
        field: item.field_id,
        cellTooltip: true,
        orderNumber: index + 1,
        enableSorting: item.field_id !== 'documents',
        width: '*',
        minWidth: 200,
        enableGrouping: false,
        cellClass:
          item['field_data_type'] === 'BIGNUMERIC' ||
          item['field_data_type'] === 'INT64'
            ? 'text-align-right'
            : 'text-align-left',
      };
    });

    const merchant_id = {
      name: 'Merchant ID',
      displayName: 'Merchant ID',
      field: 'merchant_id',
      cellTooltip: true,
      orderNumber: 0,
      width: '*',
      minWidth: 200,
      enableGrouping: false,
      cellClass: 'text-align-right',
    };
    const version = {
      name: 'Version',
      displayName: 'Version',
      field: 'version',
      cellTooltip: true,
      orderNumber: this.gridOptions.columnDefs.length,
      width: '*',
      minWidth: 200,
      enableGrouping: false,
      cellClass: 'text-align-right',
    };
    this.gridOptions.columnDefs.unshift(merchant_id);
    this.gridOptions.columnDefs.push(version);
  }

  private buildTableColumns(visible: DynamicFilters[]) {
    this.tableColumns = visible.map((f, i) => ({
      name: f.field_name,
      field: f.field_id,
      headerTooltip: '',
      visible: true,
      cellClass:
        f['field_data_type'] === 'BIGNUMERIC' ||
        f['field_data_type'] === 'INT64'
          ? 'text-align-right'
          : 'text-align-left',
      footerCellClass: '',
      minWidth: 200,
      sorting: f.field_id !== 'documents',
      orderNumber: i + 1,
    }));

    this.tableColumns.unshift({
      name: 'Merchant ID',
      field: 'merchant_id',
      headerTooltip: '',
      visible: true,
      cellClass: 'text-align-right',
      footerCellClass: '',
      minWidth: 200,
      sorting: true,
      orderNumber: 0,
    });
    this.tableColumns.push({
      name: 'Version',
      field: 'version',
      headerTooltip: '',
      visible: true,
      cellClass: 'text-align-right',
      footerCellClass: '',
      minWidth: 200,
      sorting: true,
      orderNumber: this.tableColumns.length + 1,
    });
  }

  protected runReport(offset: number, isLoadingMore = false) {
    this.showReportTable.set(true);

    this.buildTableColumns(this.visibleFieldsSignal() as DynamicFilters[]);
    this.buildOldTableColumns(this.visibleFieldsSignal() as DynamicFilters[]);

    this.offset = offset;
    this.tableLoading = true;
    this.gridOptions.dataLoading = true;
    this.cdr.detectChanges();
    this.runState = true;
    if (isLoadingMore) {
      this.gridOptions.paginationPageSize = this.limit();
      this.gridOptions.paginationCurrentPage = this.getPageFromOffset(this.offset, this.limit());
    } else {
      this.orderBy = null;
      this.orderDirection = null;
    }

    const params = {
      offset,
      limit: this.limit(),
      allFilters: this.allFieldsSignal() as DynamicFilters[],
      formData: this.filterForm.value,
      columns: this.visibleFieldsId,
      executionType: 'report',
      sorting: { field: this.orderBy, value: this.orderDirection },
      template: this.templateSignal(),
      exportReportName: REPORTS.MM_VERIFICATION.exportReportName
    };
    this.verificationService
      .runReport(params)
      .pipe(
        take(1),
        catchError(() => {
          return of([]);
        })
      )
      .subscribe((data: any) => {
        this.tableLoading = false;
        this.gridOptions.dataLoading = false;
        this.tableData = [...data];
        this.gridOptions.data = [...data];
        this.runState = false;
        this.cdr.detectChanges();
      });
    if (!isLoadingMore) {
      this.loadGrandTotal(offset);
    }
  }

  protected loadGrandTotal(offset: number) {
    if (!offset) {
      if (this.gridApi) {
        this.gridApi.grid.loadingAggregationData = true;
      }
      const params = {
        offset,
        limit: this.limit(),
        allFilters: this.allFieldsSignal() as DynamicFilters[],
        formData: this.filterForm.value,
        columns: this.visibleFieldsId,
        executionType: 'report',
        isGrandTotal: true,
        sorting: { field: this.orderBy, value: this.orderDirection },
        template: this.templateSignal(),
        exportReportName: REPORTS.MM_VERIFICATION.exportReportName
      };
      this.verificationService
        .runReport({
          ...params,
          isGrandTotal: true,
        })
        .pipe(
          take(1),
          catchError(() => of([]))
        )
        .subscribe((data) => {
          this.generalTotal = data[0]?.rows_count;
          if (this.gridApi) {
            this.gridApi.grid.total =
              data && data[0] && data[0].rows_count ? data[0].rows_count : 0;
            this.gridOptions.totalItems =
              data && data[0] && data[0].rows_count ? data[0].rows_count : 0;
            this.gridApi.grid.loadingAggregationData = false;
          }
          this.cdr.detectChanges();
        });
    }
  }

  protected onSort(sortingValue: {
    field: string | null;
    value: 'asc' | 'desc' | null;
  }): void {
    this.offset = 0;
    this.tableLoading = true;
    this.gridOptions.dataLoading = true;
    this.orderBy = sortingValue.field;
    this.orderDirection = sortingValue.value;
    this.gridOptions.paginationCurrentPage = this.getPageFromOffset(this.offset, this.limit());
    this.verificationService
      .runReport({
        offset: 0,
        limit: this.limit(),
        allFilters: this.allFieldsSignal() as DynamicFilters[],
        formData: this.filterForm.value,
        columns: this.visibleFieldsId,
        executionType: 'report',
        isGrandTotal: false,
        sorting: sortingValue,
        template: this.templateSignal(),
        exportReportName: REPORTS.MM_VERIFICATION.exportReportName,
      })
      .pipe(
        take(1),
        catchError(() => of([]))
      )
      .subscribe((data) => {
        this.tableData = [...data];
        this.gridOptions.data = [...data];
        this.tableLoading = false;
        this.gridOptions.dataLoading = false;
        this.cdr.detectChanges();
      });
  }

  protected downloadCsv(template?: ReportTemplate) {
    if (!template) this.csvLoading.set(true);
    this.verificationService
      .runReport({
        offset: 0,
        limit: this.limit(),
        allFilters: template
          ? template.input_params.allFields
          : (this.allFieldsSignal() as DynamicFilters[]),
        formData: template
          ? template.input_params.fieldValues
          : this.filterForm.value,
        columns: template
          ? template.input_params.visibleFields
          : this.visibleFieldsId,
        executionType: 'csv_load',
        isGrandTotal: false,
        sorting: { field: null, value: null },
        template: template || this.templateSignal(),
        exportReportName: REPORTS.MM_VERIFICATION.exportReportName
      })
      .pipe(
        take(1),
        switchMap((data) => this.verificationService.csvLoad(data[0].path)),
        catchError((e) => {
          this.csvLoading.set(false);
          if (template) {
            this.templateCsvLoadingConfig.set({
              templateId: template.template_id,
              isLoading: false,
            });
          }
          return e;
        })
      )
      .subscribe((res: any) => {
        const templateName = `Report_${
          template?.template_name ||
          (this.templateSignal() as ReportTemplate).template_name ||
          ''
        }_${moment().format('YYYY-MM-DD_HH:mm')}.csv`;
        downloadURL(res, templateName);
        this.csvLoading.set(false);
        if (template) {
          this.templateCsvLoadingConfig.set({
            templateId: template.template_id,
            isLoading: false,
          });
        }
      });
  }

  private groupLookups(data: Lookups[]): { [k: string]: Lookups[] } {
    return data.reduce((acc, cur) => {
      (acc[cur.param_key] = acc[cur.param_key] || []).push(cur);
      return acc;
    }, {} as Record<string, Lookups[]>);
  }

  protected sortGroupedLookups(groupedLookups: { [key: string]: Lookups[] }) {
    Object.keys(groupedLookups).forEach((key) => {
      if (Array.isArray(groupedLookups[key])) {
        if (groupedLookups[key].some(({ sort_key }) => sort_key && sort_key > 0)) {
          groupedLookups[key].sort((a, b) =>
            a.sort_key > b.sort_key ? 1 : -1
          );
        } else {
          groupedLookups[key].sort((a, b) =>
            a.lookup_value > b.lookup_value ? 1 : -1
          );
        }
      }
    });
  }

  protected getControl(fieldId: string): any {
    const control = this.filterForm.get(fieldId);
    return control instanceof FormControl ? control : null;
  }

  getFilledFieldNames() {
    return (this.allFieldsSignal() as DynamicFilters[])
      .filter(({ field_id }) => {
        const value = this.filterForm.value[field_id];
        if (value == null) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === 'string') {
          return value.length > 0;
        }
        return true;
      })
      .map(({ field_name }) => field_name)
      .join(', ');
  }

  protected clearFilters(): void {
    const defaults: Record<string, any> = {
      merchant_name: this.domainLookupsSignal()?.length
        ? this.domainLookupsSignal()!.map((l) => l.lookup_key)
        : [],
      show_last_verification: true,
    };
    this.filterForm.reset(defaults);
    this.applyDefaultValues(true, false);
    this.filterForm.markAsPristine();
    this.filterForm.markAsUntouched();
  }

  public onToggleChange(event: ToggleSwitchChangeEvent) {
    this.gridOptions.isVisible = event.checked;
    this.cdr.detectChanges();
  }

  public onDocumentsClear(): void {
    this.getControl('documents').reset();
    this.getControl('documents_type').reset();
    this.getControl('documents_status').reset();
    this.getControl('documents_expiry_date').reset();
    this.getControl('documents_created_date').reset();
  }

  get visibleFieldsId(): string[] {
    if (this.visibleFieldsSignal()) {
      return (this.visibleFieldsSignal() as DynamicFilters[]).map(
        (f) => f.field_id
      );
    } else {
      return [];
    }
  }

  protected openSaveTemplateModal() {
    this.domainsOverloadSaveMode = true;
    this.domainsOverloadAction = 'Save';
    if (this.isAllDomainsSelected) {
      this.domainsOverloadVisible.set(true);
    } else {
      this.modalVisible.set(true);
    }
  }

  protected runOverloadCheck(action: 'Run' | 'Download') {
    this.domainsOverloadSaveMode = false;
    if (this.isAllDomainsSelected) {
      this.domainsOverloadAction = action;
      this.domainsOverloadVisible.set(true);
    } else {
      if (action == 'Run') {
        this.saveTemporaryTemplate();
        this.runReport(0, false);
      } else if (action == 'Download') {
        this.downloadCsv();
      }
    }
  }

  protected domainsOverloadConfirm(event: {confirm: boolean, isSaveMode: boolean}): void {
    const {confirm, isSaveMode} = event
    if (confirm && isSaveMode) {
      this.modalVisible.set(true);
    } else if(confirm){
      if (this.domainsOverloadAction == 'Run') {
        this.saveTemporaryTemplate();
        this.runReport(0, false);
      } else if (this.domainsOverloadAction == 'Download') {
        this.downloadCsv();
      }
    }
  }

  get isAllDomainsSelected(): boolean {
    if (
      !this.domainLookupsSignal()?.length ||
      this.domainLookupsSignal()?.length == 1 ||
      this.sessionService.sessionData?.Domains && this.sessionService.sessionData.Domains != '1000'
    )
      return false;
    return (
      this.domainLookupsSignal()?.length ===
      this.filterForm?.get('merchant_name')?.value?.length
    );
  }

  get isFilterFormValid(): boolean {
    return this.filterForm?.valid 
    && this.filterForm?.get('merchant_name')?.value?.length > 0  
    && ((this.filterForm?.get('verification_creation_date')?.value?.start && this.filterForm?.get('verification_creation_date')?.value?.end)
    || (this.filterForm?.get('verification_updated_date')?.value?.start && this.filterForm?.get('verification_updated_date')?.value?.end));
  }

  get isTemplateSavingEnabled(): boolean {
    return this.isFilterFormValid && !this.isTemplateSaved(); 
  }

  protected applyConfigurationFields(event: {
    fieldsToShow: DynamicFilters[];
    allFields: DynamicFilters[];
  }) {
    this.isTemplateSaved.set(false);
    this.visibleFieldsSignal.set(event.fieldsToShow);
    this.applyDefaultValues(false, true);
  }

  protected onOpenDocumentsModal() {
    this.templateSavedStateSnapshot = this.isTemplateSaved();
    this.snapshotDocumentsControls();
  }

  protected onCancelDocumentsModal() {
    this.restoreDocumentsControls();
    this.clearDocumentSnapshots();
    this.isTemplateSaved.set(this.templateSavedStateSnapshot);
  }

  protected applyDocumentField() {
    this.clearDocumentSnapshots();
    const fieldsMap: Record<string, string> = {
      documents_type: 'Type',
      documents_status: 'Status',
      documents_expiry_date: 'Expiry Date',
      documents_created_date: 'Updated Date',
    };

    const currentValue = this.normalizeDocumentsVal(this.filterForm.get('documents')?.value);
    const newValue = Object.entries(fieldsMap)
      .filter(([field]) => Array.isArray(this.filterForm.get(field)?.value) ? this.filterForm.get(field)?.value.length > 0 : !!this.filterForm.get(field)?.value)
      .map(([, label]) => label)
      .join(', ');

    if(currentValue != newValue) {
      this.filterForm.get('documents')?.patchValue(newValue);
    } else {
      this.isTemplateSaved.set(this.templateSavedStateSnapshot);
    }
  }

  private normalizeDocumentsVal(value: unknown): string {
    return value == null ? '' : String(value);
  }

  private allFieldsForConfiguration(data: DynamicFilters[]) {
    this.allConfigurationFields = data.filter(
      (item) => !this.documentFieldIds.includes(item.field_id)
    );
  }

  private getPageFromOffset(offset: number, limit: number) {
    return offset ? Math.floor(offset / limit) + 1 : 1;
  }

  private clearTemplate() {
    if (this.filterForm) this.clearFilters();
    this.templateSignal.set(null);
    this.templateService.setSearchParams({
      modificationId: null,
      templateName: null,
      templateId: null,
    });
  }

  protected applyTableHeight(isPanelOpened: boolean) {
    if (this.gridOptions) {
      this.gridOptions.scrollHeight = isPanelOpened ? 360 : 480;
    }
    this.scrollHeight = isPanelOpened ? 350 : 400;
  }

  protected applyDefaultValues(onClearFilters = false, onApplyConfigurationFields = false):void{
    if(!this.filterForm || !this.visibleFieldsSignal()) return;
    const prevVisibleFieldsIds = this.prevVisibleFields.map(({field_id}) => field_id);
    const visibleFields = this.getAllVisibleFields();
    const fieldsWithDateRange = this.visibleFieldsSignal().filter((item: DynamicFilters) => item.field_id !== 'verification_updated_date' && item.field_data_type === 'TIMESTAMP');

    Object.keys(this.filterForm.controls).forEach((control)=> {
      const isFieldVisible = !!visibleFields.find((item: DynamicFilters)=> item['field_id'] == control);
      const isSetDefaultDateRange = !!fieldsWithDateRange.find((item: DynamicFilters)=> item['field_id'] == control);
      if (
        isFieldVisible &&
        isSetDefaultDateRange &&
        (
          onClearFilters ||
          (
            onApplyConfigurationFields &&
            !prevVisibleFieldsIds.includes(control)
          )
        )
      ) {
        this.filterForm.controls[control].reset({
          alias: 'custom',
          start: moment().startOf('day').toDate(),
          end: moment().endOf('day').toDate(),
        });
      } else if(onApplyConfigurationFields && !isFieldVisible && this.getControl(control)?.value && control !== 'show_last_verification') {
        this.filterForm.controls[control].reset();
      }
    })
  }

  get templateLabel(): string {
    const queryParams = new URLSearchParams(window.location.search);
    const modificationId = queryParams.get('modificationId') || '';
    const templateName = queryParams.get('templateName') || '';
    return this.templateSignal()?.is_temporary && !modificationId ? '' : this.templateSignal()?.template_name || (templateName ? TEMPORARY_TEMPLATE_WARNING + templateName : '');
  }

  private nullUndefinedOrEmpty(value: any): any {
    return value === null || value === undefined || value === '';
  }

  private getAllVisibleFields(): DynamicFilters[] {
    const allFields = this.allFieldsSignal() || [];
    const isDocumentsFieldSelected = !!this.visibleFieldsSignal().find((item: DynamicFilters)=> item['field_id'] == 'documents');
    const documentsFields = allFields.filter((item: DynamicFilters) => this.documentFieldIds.includes(item.field_id))
    return !isDocumentsFieldSelected ? [...this.visibleFieldsSignal()] : [...this.visibleFieldsSignal(), ...documentsFields];
  }

  public setPrevVisibleFields(): void {
    this.prevVisibleFields = [...this.getAllVisibleFields()];
  }

  private createFormSnapshot(control: AbstractControl): FormSnapshot {
    return {
      value: control.getRawValue(),
      touched: control.touched,
      dirty: control.dirty,
    };
  }

  private restoreFormSnapshot(control: AbstractControl, snapshot: FormSnapshot): void {
    control.reset(snapshot.value, { emitEvent: false });
    snapshot.touched ? control.markAllAsTouched() : control.markAsUntouched();
    snapshot.dirty ? control.markAsDirty() : control.markAsPristine();
  }

  private snapshotDocumentsControls(): void {
    this.documentFieldIds
    .filter((field) => this.filterForm.get(field))
    .forEach((key) => {
      this.controlSnapshots.set(key, this.createFormSnapshot(this.filterForm.get(key)));
    });
    this.filterFormSnapshot = this.createFormSnapshot(this.filterForm);
  }

  private restoreDocumentsControls(): void {
    this.documentFieldIds
    .filter((field) => this.filterForm.get(field) && this.controlSnapshots.get(field))
    .forEach((key) => {
      const controlSnapshot = this.controlSnapshots.get(key);
      if(controlSnapshot)  this.restoreFormSnapshot(this.filterForm.get(key), controlSnapshot)
    });

    if(this.filterFormSnapshot) {
      this.restoreFormSnapshot(this.filterForm, this.filterFormSnapshot);
    }
  }

  private clearDocumentSnapshots(): void {
    this.filterFormSnapshot = null;
    this.controlSnapshots.clear();
  }
}
